// src/context/AuthContext.jsx
// FINAL FIXED - NO RANDOM AUTO LOGOUT / SINGLE-FLIGHT REFRESH / COPY-PASTE READY
// ✅ RBAC perms from backend
// ✅ Memory-only branch
// ✅ NO localStorage branch persistence
// ✅ Safari-safe stable device_id (localStorage + cookie + window fallback)
// ✅ Dynamic login by user/email (single login request; no branch-guess loop)
// ✅ Lock branch ONLY after successful login when server/user gives one concrete branch
// ✅ All auth requests include x-branch via live apiBase branch
// ✅ apiFetch/apiJSON exposed to app
// ✅ Safe refresh + safe JSON/text parsing
// ✅ Prevents random logout on temporary refresh/network issues
// ✅ Single-flight refresh (only one refresh request at a time)
// ✅ Does NOT clear user unless backend truly says unauthenticated

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import {
  API,
  setBranch,
  clearBranch,
  getActiveBranch,
} from "../apiBase";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const LOCAL_KEY = "role_perms_overrides_v1";
const REFRESH_RETRY_GRACE_MS = 5000;

/* ===== Permissions (gate buttons/routes) ===== */
export const PERMISSION_KEYS = [
  "create_table",
  "open_saved_tables",
  "merge_all",
  "end_of_day",
  "options_open",
  "user_create",
  "nav_home",
  "nav_search_date",
  "nav_merge_by_date_all",
  "nav_birthdays",
  "nav_reservations",
  "nav_guest_search",
  "nav_control",
  "nav_competitions",
  "nav_all_data",
  "auth_signin",
  "auth_logout",
  "send_all",
  "nav_customer_service",
  "remote_assist_share",
  "remote_assist_view",
  "nav_menu_prices",
  "nav_menu_calibrator",
  "nav_inventory",
  "nav_accounting",
  "accounting_dashboard",
  "accounting_accounts",
  "accounting_journal",
  "accounting_invoices",
  "accounting_reports",
];

/* ===== Legacy fallback role permissions ===== */
const DEFAULT_ROLE_PERMS = {
  admin: ["*"],
  staff: [
    "create_table",
    "open_saved_tables",
    "merge_all",
    "options_open",
    "nav_home",
    "nav_search_date",
    "nav_birthdays",
    "nav_reservations",
    "nav_guest_search",
    "nav_control",
    "nav_all_data",
    "auth_signin",
    "auth_logout",
    "nav_customer_service",
    "nav_menu_prices",
    "user_create",
  ],
};

const ENV_BRANCH_HEADER = (import.meta?.env?.VITE_BRANCH_HEADER || "").trim();
const ENV_BRANCH_VALUE = (import.meta?.env?.VITE_BRANCH_VALUE || "").trim();

const KNOWN_BRANCHES = ["jounieh", "tripoli", "batroun", "zgharta", "ehden"];
const KNOWN_BRANCH_SET = new Set(KNOWN_BRANCHES);

/* =============================================================================
   Helpers
============================================================================= */
function safeTrim(v) {
  return String(v || "").trim();
}

function safeLower(v) {
  return safeTrim(v).toLowerCase();
}

function safeUpper(v) {
  return safeTrim(v).toUpperCase();
}

function normalize(u) {
  return String(u || "").replace(/\/+$/, "");
}

function joinUrl(base, path) {
  const b = normalize(base);
  const p = String(path || "");
  if (!p) return b;
  if (/^https?:\/\//i.test(p)) return p;
  return `${b}${p.startsWith("/") ? p : `/${p}`}`;
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getCookie(name) {
  try {
    if (typeof document === "undefined") return "";
    const rx = new RegExp(`(?:^|; )${escapeRegex(name)}=([^;]*)`);
    const m = document.cookie.match(rx);
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
}

function setCookie(name, value, days = 3650) {
  try {
    if (typeof document === "undefined") return;
    const maxAge = Math.max(1, Number(days || 3650)) * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {}
}

async function readBodySafe(res) {
  const ct = res.headers.get("Content-Type") || "";
  const isJson = ct.includes("application/json");

  if (res.status === 204) return null;

  if (isJson) {
    const text = await res.text().catch(() => "");
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  const text = await res.text().catch(() => "");
  return text || null;
}

function isLikelyCorsOrNetwork(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed") ||
    msg.includes("typeerror:")
  );
}

function normalizeRoles(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map(String).map((r) => r.toLowerCase().trim()).filter(Boolean);
  }

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(String).map((r) => r.toLowerCase().trim()).filter(Boolean);
      }
    } catch {}

    return raw
      .split(",")
      .map((r) => r.toLowerCase().trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeApiErrorMessage(data, fallback = "Request failed") {
  if (!data) return fallback;

  if (typeof data === "string") return data;

  const d = data?.detail ?? data?.message ?? data?.error ?? data?.raw ?? data;

  if (typeof d === "string") return d;

  if (Array.isArray(d)) {
    return d
      .map((it) => {
        const loc = Array.isArray(it?.loc) ? it.loc.join(".") : "";
        const msg = it?.msg || "Invalid input.";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join("\n");
  }

  try {
    return JSON.stringify(d);
  } catch {
    return fallback;
  }
}

function computeHostessName(u) {
  if (!u) return "";

  const fullName = safeTrim(u.full_name || u.display_name);
  if (fullName) return fullName;

  const first = safeTrim(u.first_name || u.firstName);
  const last = safeTrim(u.last_name || u.lastName);
  const full = `${first} ${last}`.trim();
  if (full) return full;

  const username = safeTrim(u.username);
  if (username) return username;

  const email = safeTrim(u.email);
  if (email) return email;

  const sub = safeTrim(u.sub || u.user || u.name);
  if (sub) return sub;

  return "";
}

function isPlainObject(value) {
  if (value == null || typeof value !== "object") return false;
  if (Array.isArray(value)) return false;
  if (typeof FormData !== "undefined" && value instanceof FormData) return false;
  if (typeof Blob !== "undefined" && value instanceof Blob) return false;
  if (typeof ArrayBuffer !== "undefined" && value instanceof ArrayBuffer) return false;
  if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) return false;
  return true;
}

function buildBodyAndHeaders(init = {}) {
  const headers = { ...(init.headers || {}) };
  let body = init.body;

  const hasContentType =
    Object.prototype.hasOwnProperty.call(headers, "Content-Type") ||
    Object.prototype.hasOwnProperty.call(headers, "content-type");

  if (body != null && isPlainObject(body)) {
    body = JSON.stringify(body);
    if (!hasContentType) headers["Content-Type"] = "application/json";
  }

  return { headers, body };
}

function pickSingleConcreteBranch(branches) {
  const arr = Array.isArray(branches) ? branches : [];
  if (arr.includes("*")) return "";

  const concrete = arr
    .map(safeLower)
    .filter((b) => KNOWN_BRANCH_SET.has(b));

  return concrete.length === 1 ? concrete[0] : "";
}

function extractServerBranch(...items) {
  for (const item of items) {
    const b = safeLower(
      item?.branch ||
        item?.active_branch ||
        item?.city ||
        item?.branch_name ||
        item?.user?.branch ||
        item?.user?.active_branch ||
        item?.user?.city ||
        item?.user?.branch_name ||
        ""
    );
    if (KNOWN_BRANCH_SET.has(b)) return b;
  }
  return "";
}

function extractServerBranches(...items) {
  for (const item of items) {
    const raw =
      item?.branches ||
      item?.allowed_branches ||
      item?.user?.branches ||
      item?.user?.allowed_branches ||
      [];

    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
      return raw.split(",").map((x) => x.trim()).filter(Boolean);
    }
  }
  return [];
}

function normalizedAllowedBranches(...items) {
  const raw = extractServerBranches(...items)
    .map(safeLower)
    .filter(Boolean);

  if (raw.includes("*")) return [...KNOWN_BRANCHES];

  const out = [];
  for (const b of raw) {
    if (KNOWN_BRANCH_SET.has(b) && !out.includes(b)) out.push(b);
  }

  const serverBranch = extractServerBranch(...items);
  if (!out.length && serverBranch) out.push(serverBranch);
  return out;
}

function resolveBranchPolicy(userObj, responseObj = null) {
  const rawBranches = extractServerBranches(userObj, responseObj)
    .map(safeLower)
    .filter(Boolean);

  const admin = normalizeRoles(userObj?.roles || responseObj?.user?.roles).includes("admin");
  const allowed = admin || rawBranches.includes("*")
    ? [...KNOWN_BRANCHES]
    : normalizedAllowedBranches(userObj, responseObj);

  const serverBranch = admin ? "" : extractServerBranch(userObj, responseObj);
  const current = safeLower(getActiveBranch());

  let active = allowed.includes(current) ? current : "";
  if (!active && allowed.includes(serverBranch)) active = serverBranch;
  if (!active) active = allowed[0] || "";

  return {
    admin,
    allowed,
    active,
    canSwitch: allowed.length > 1,
  };
}

/* =============================================================================
   Device id (Safari-safe)
============================================================================= */
const DEVICE_KEY = "device_id_v1";

function makeDeviceId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {}
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getOrMakeDeviceId() {
  let did = "";

  try {
    did = localStorage.getItem(DEVICE_KEY) || "";
  } catch {}

  if (!did) {
    try {
      did = getCookie(DEVICE_KEY) || "";
    } catch {}
  }

  if (!did) {
    try {
      did = window.__BAMBOO_DEVICE_ID__ || "";
    } catch {}
  }

  if (!did) {
    did = makeDeviceId();
  }

  try {
    localStorage.setItem(DEVICE_KEY, did);
  } catch {}

  try {
    setCookie(DEVICE_KEY, did);
  } catch {}

  try {
    window.__BAMBOO_DEVICE_ID__ = did;
  } catch {}

  return String(did);
}

/* =============================================================================
   Branch headers
============================================================================= */
function applyBranchHeaders(headers) {
  const h = { ...(headers || {}) };

  const hasXBranch =
    Object.prototype.hasOwnProperty.call(h, "x-branch") ||
    Object.prototype.hasOwnProperty.call(h, "X-Branch");

  if (!hasXBranch) {
    const b = safeLower(getActiveBranch());
    if (b) {
      h["x-branch"] = b;
      h["X-Branch"] = b;
    }
  }

  if (ENV_BRANCH_HEADER && ENV_BRANCH_VALUE) {
    if (!Object.prototype.hasOwnProperty.call(h, ENV_BRANCH_HEADER)) {
      h[ENV_BRANCH_HEADER] = ENV_BRANCH_VALUE;
    }
  }

  return h;
}

/* =============================================================================
   Provider
============================================================================= */
export function AuthProvider({ children }) {
  const API_BASE = normalize(API);

  if (typeof window !== "undefined") {
    console.log("[Auth] Using API_BASE:", API_BASE);
    console.log("[Auth] ACTIVE BRANCH:", safeLower(getActiveBranch() || ""));
  }

  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [activeBranch, setActiveBranchState] = useState(() => safeLower(getActiveBranch() || ""));

  const [serverPerms, setServerPerms] = useState(null);
  const [serverRbacRoles, setServerRbacRoles] = useState([]);

  const [rolePerms, setRolePermsState] = useState(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (raw) return { ...DEFAULT_ROLE_PERMS, ...JSON.parse(raw) };
    } catch {}
    return { ...DEFAULT_ROLE_PERMS };
  });

  const refreshInFlight = useRef(null);
  const mountedRef = useRef(true);
  const authKnownBadRef = useRef(false);
  const lastRefreshFailAtRef = useRef(0);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LOCAL_KEY) {
        try {
          const parsed = e.newValue ? JSON.parse(e.newValue) : null;
          if (parsed) setRolePermsState({ ...DEFAULT_ROLE_PERMS, ...parsed });
        } catch {}
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setRolePerms = useCallback((updater) => {
    setRolePermsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  const clearAuthStateHard = useCallback(() => {
    setUser(null);
    setServerPerms(null);
    setServerRbacRoles([]);
    setActiveBranchState("");
    clearBranch({ reload: false, reason: "auth-clear" });
  }, []);

  const refresh = useCallback(async () => {
    if (refreshInFlight.current) return refreshInFlight.current;

    const p = (async () => {
      const url = joinUrl(API_BASE, "/auth/refresh");
      const csrf =
        getCookie("csrf_token") ||
        getCookie("csrf") ||
        getCookie("XSRF-TOKEN") ||
        "";

      const headers = applyBranchHeaders({
        "Content-Type": "application/json",
        ...(csrf ? { "x-csrf-token": csrf } : {}),
      });

      let res;
      try {
        res = await fetch(url, {
          method: "POST",
          headers,
          credentials: "include",
        });
      } catch (err) {
        lastRefreshFailAtRef.current = Date.now();
        throw err;
      }

      const data = await readBodySafe(res);

      if (!res.ok) {
        const msg = normalizeApiErrorMessage(data, "Refresh failed");

        if (res.status === 401) {
          authKnownBadRef.current = true;
          const e = new Error(msg);
          e.status = 401;
          e.authBad = true;
          throw e;
        }

        lastRefreshFailAtRef.current = Date.now();
        const e = new Error(msg);
        e.status = res.status;
        throw e;
      }

      authKnownBadRef.current = false;
      return true;
    })();

    refreshInFlight.current = p;
    try {
      return await p;
    } finally {
      refreshInFlight.current = null;
    }
  }, [API_BASE]);

  const apiFetch = useMemo(() => {
    return async (pathOrUrl, opts = {}) => {
      const url = String(pathOrUrl || "").startsWith("http")
        ? String(pathOrUrl)
        : joinUrl(API_BASE, pathOrUrl);

      const prepared = buildBodyAndHeaders(opts);
      const hasBody = prepared.body !== undefined && prepared.body !== null;
      const method = safeUpper(opts.method || "") || (hasBody ? "POST" : "GET");

      const headers = applyBranchHeaders(prepared.headers);

      if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const csrf =
          getCookie("csrf_token") ||
          getCookie("csrf") ||
          getCookie("XSRF-TOKEN") ||
          "";
        if (csrf && !headers["x-csrf-token"]) {
          headers["x-csrf-token"] = csrf;
        }
      }

      const doReq = async () =>
        fetch(url, {
          ...opts,
          method,
          headers,
          body: prepared.body,
          credentials: "include",
        });

      let res;

      try {
        res = await doReq();
      } catch (err) {
        if (isLikelyCorsOrNetwork(err)) {
          throw new Error(
            [
              "❌ Network/CORS error (Failed to fetch).",
              `URL: ${url}`,
              "Likely causes:",
              "• Backend not running",
              "• CORS not allowing your origin",
              "• Cookies blocked (Allow-Credentials missing)",
              "Fix: ensure FastAPI echoes Origin and sets Allow-Credentials: true.",
            ].join("\n")
          );
        }
        throw err;
      }

      if (res.status !== 401) {
        return res;
      }

      if (authKnownBadRef.current) {
        clearAuthStateHard();
        return res;
      }

      try {
        await refresh();
        res = await doReq();
        return res;
      } catch (err) {
        if (err?.authBad || err?.status === 401) {
          clearAuthStateHard();
        } else {
          console.warn("[Auth] refresh failed temporarily; keeping current user session in UI.", err);
        }
        return res;
      }
    };
  }, [API_BASE, refresh, clearAuthStateHard]);

  const apiJSON = useMemo(() => {
    return async (pathOrUrl, opts = {}) => {
      const res = await apiFetch(pathOrUrl, opts);
      const data = await readBodySafe(res);

      if (!res.ok) {
        const msg = normalizeApiErrorMessage(data, "Request failed");
        const err = new Error(msg);
        err.userMessage = msg;
        err.status = res.status;
        err.data = data;
        err.response = res;
        throw err;
      }

      return data;
    };
  }, [apiFetch]);

  const fetchMe = useCallback(async () => {
    const url = joinUrl(API_BASE, "/auth/me");

    let res;
    try {
      res = await fetch(url, {
        method: "GET",
        headers: applyBranchHeaders({}),
        credentials: "include",
      });
    } catch (err) {
      throw err;
    }

    const data = await readBodySafe(res);

    if (!res.ok) {
      const e = new Error(normalizeApiErrorMessage(data, "Not authenticated"));
      e.status = res.status;
      throw e;
    }

    let u = data?.user || data || null;

    // Admins are global branch operators in the frontend. This also protects
    // the UI from an old/stale session that still says "jounieh".
    if (u && normalizeRoles(u?.roles).includes("admin")) {
      u = {
        ...u,
        branches: ["*"],
        city: "",
        branch: "",
        active_branch: "",
      };
    }

    setUser(u);

    // Resolve one authoritative branch. No lock/unlock mechanism exists in V4.
    const policy = resolveBranchPolicy(u, data);
    setBranch(policy.active, { reload: false, reason: "auth-policy" });
    setActiveBranchState(policy.active);

    try {
      window.dispatchEvent(
        new CustomEvent("bamboo:branchReady", {
          detail: {
            branch: policy.active,
            allowed: policy.allowed,
            canSwitch: policy.canSwitch,
          },
        })
      );
    } catch {}

    authKnownBadRef.current = false;
    return u;
  }, [API_BASE]);

  const fetchPerms = useCallback(async (userArg = null) => {
    const activeUser = userArg || user;

    if (!activeUser) {
      setServerPerms(null);
      setServerRbacRoles([]);
      return [];
    }

    const authRoles = normalizeRoles(activeUser?.roles);
    if (authRoles.includes("admin")) {
      setServerPerms(["*"]);
      setServerRbacRoles(["admin"]);
      return ["*"];
    }

    try {
      const data = await apiJSON("/rbac/my-perms", { method: "GET" });

      const perms =
        (Array.isArray(data?.perms) ? data.perms : null) ||
        (Array.isArray(data?.permissions) ? data.permissions : null) ||
        (Array.isArray(data) ? data : []);

      const roles =
        (Array.isArray(data?.roles) ? data.roles : null) ||
        (Array.isArray(data?.rbac_roles) ? data.rbac_roles : null) ||
        [];

      const cleanedPerms = (perms || [])
        .map(String)
        .map((p) => p.trim())
        .filter(Boolean);

      const cleanedRoles = (roles || [])
        .map(String)
        .map((r) => r.toLowerCase().trim())
        .filter(Boolean);

      setServerPerms(cleanedPerms);
      setServerRbacRoles(cleanedRoles);
      return cleanedPerms;
    } catch (e) {
      const msg = String(e?.userMessage || e?.message || "");
      const status = e?.status;

      console.warn("[Auth] RBAC perms not available, falling back to local rolePerms.", {
        status,
        msg,
      });

      setServerPerms(null);
      setServerRbacRoles([]);
      return [];
    }
  }, [apiJSON, user]);

  const login = useCallback(
    async (email, password) => {
      const url = "/auth/login";
      const ctrl = new AbortController();
      const timeout = setTimeout(() => ctrl.abort(), 15000);

      const cleanEmail = safeLower(email);

      clearBranch({ reload: false, reason: "login-start" });
      setActiveBranchState("");
      setServerPerms(null);
      setServerRbacRoles([]);
      setUser(null);
      authKnownBadRef.current = false;

      const stableDeviceId = getOrMakeDeviceId();

      const payload = {
        username: cleanEmail,
        email: cleanEmail,
        password: String(password || ""),
        device_id: stableDeviceId,
      };

      try {
        console.log("[Auth] LOGIN email:", cleanEmail);
        console.log("[Auth] device_id:", stableDeviceId);

        const data = await apiJSON(url, {
          method: "POST",
          signal: ctrl.signal,
          body: payload,
        });

        const me = await fetchMe();

        if (!me) {
          throw new Error("Login succeeded but failed to load current user.");
        }

        // fetchMe() already selected the authoritative branch.
        await fetchPerms(me);
        return data;
      } catch (err) {
        clearBranch({ reload: false, reason: "login-failed" });
        setActiveBranchState("");
        setServerPerms(null);
        setServerRbacRoles([]);
        setUser(null);

        if (String(err?.name || "") === "AbortError") {
          const e = new Error(
            `Login timed out after 15s. API: ${joinUrl(API_BASE, "/auth/login")}`
          );
          e.userMessage = e.message;
          throw e;
        }

        err.userMessage = String(err?.userMessage || err?.message || "Login failed.");
        throw err;
      } finally {
        clearTimeout(timeout);
      }
    },
    [API_BASE, apiJSON, fetchMe, fetchPerms]
  );

  const logout = useCallback(async () => {
    try {
      await apiJSON("/auth/logout", { method: "POST" });
    } catch {
      // ignore
    } finally {
      clearAuthStateHard();
      authKnownBadRef.current = true;
    }
  }, [apiJSON, clearAuthStateHard]);

  useEffect(() => {
    mountedRef.current = true;

    (async () => {
      try {
        const u = await fetchMe();
        if (u) await fetchPerms(u);
      } catch (err1) {
        const status1 = Number(err1?.status || 0);
        const recentlyFailedRefresh =
          Date.now() - lastRefreshFailAtRef.current < REFRESH_RETRY_GRACE_MS;

        if (status1 === 401 && !recentlyFailedRefresh) {
          try {
            await refresh();
            const u = await fetchMe();
            if (u) await fetchPerms(u);
          } catch (err2) {
            if (err2?.authBad || err2?.status === 401) {
              clearAuthStateHard();
            } else {
              console.warn("[Auth] bootstrap temporary failure; keeping UI session state.", err2);
            }
          }
        } else if (status1 === 401) {
          clearAuthStateHard();
        } else {
          console.warn("[Auth] bootstrap network/server issue; not forcing logout.", err1);
        }
      } finally {
        if (mountedRef.current) setBooting(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchMe, fetchPerms, refresh, clearAuthStateHard]);

  const roles = useMemo(() => normalizeRoles(user?.roles), [user]);

  const hasRole = useMemo(() => {
    return (...needed) => {
      const want = needed.map((r) => safeLower(r));
      return want.some((r) => roles.includes(r));
    };
  }, [roles]);

  const isAdmin = useMemo(() => hasRole("admin"), [hasRole]);

  const perms = useMemo(() => {
    if (!roles.length) return [];

    if (Array.isArray(serverPerms)) {
      return serverPerms;
    }

    if (roles.includes("admin")) return ["*"];

    const set = new Set();
    for (const r of roles) {
      const list = rolePerms[r] || [];
      for (const p of list) set.add(p);
    }
    return Array.from(set);
  }, [roles, rolePerms, serverPerms]);

  const hasPerm = useMemo(() => {
    return (perm) => perms.includes("*") || perms.includes(String(perm));
  }, [perms]);

  const hostessName = useMemo(() => computeHostessName(user), [user]);

  const availableBranches = useMemo(() => {
    if (!user) return [];
    const policy = resolveBranchPolicy(user);
    return policy.allowed;
  }, [user]);

  const canSwitchBranch = availableBranches.length > 1;

  const switchBranch = useCallback(
    (nextRaw) => {
      const next = safeLower(nextRaw);

      if (!user) {
        const e = new Error("Not authenticated");
        e.status = 401;
        throw e;
      }

      if (!KNOWN_BRANCH_SET.has(next)) {
        throw new Error(`Invalid branch: ${nextRaw}`);
      }

      if (!availableBranches.includes(next)) {
        const e = new Error(`You are not allowed to access branch '${next}'.`);
        e.status = 403;
        throw e;
      }

      setBranch(next, { reload: false, reason: "user-switch" });
      setActiveBranchState(next);

      try {
        window.dispatchEvent(
          new CustomEvent("bamboo:branchReady", {
            detail: { branch: next, allowed: availableBranches, canSwitch: canSwitchBranch },
          })
        );
      } catch {}

      return next;
    },
    [user, availableBranches.join("|"), canSwitchBranch]
  );

  useEffect(() => {
    const sync = (event) => {
      const next = safeLower(event?.detail?.branch || getActiveBranch() || "");
      if (next !== activeBranch) setActiveBranchState(next);
    };

    window.addEventListener("bamboo:branch", sync);
    window.addEventListener("app:branch", sync);
    return () => {
      window.removeEventListener("bamboo:branch", sync);
      window.removeEventListener("app:branch", sync);
    };
  }, [activeBranch]);

  useEffect(() => {
    if (!user || !availableBranches.length) return;
    if (availableBranches.includes(activeBranch)) return;

    const next = availableBranches[0];
    setBranch(next, { reload: false, reason: "policy-fallback" });
    setActiveBranchState(next);
  }, [user, activeBranch, availableBranches.join("|")]);

  const value = useMemo(
    () => ({
      token: "",
      user,
      roles,

      rbacRoles: serverRbacRoles,
      serverPermsLoaded: Array.isArray(serverPerms),

      perms,
      isAdmin,
      booting,
      isAuthenticated: !!user,

      activeBranch,
      availableBranches,
      canSwitchBranch,
      switchBranch,

      hostessName,

      rolePerms,
      setRolePerms,

      hasRole,
      hasPerm,

      login,
      logout,
      fetchMe,
      fetchPerms,
      refresh,

      apiFetch,
      apiJSON,

      API_BASE,
    }),
    [
      user,
      roles,
      serverRbacRoles,
      serverPerms,
      perms,
      isAdmin,
      booting,
      activeBranch,
      availableBranches,
      canSwitchBranch,
      switchBranch,
      hostessName,
      rolePerms,
      setRolePerms,
      hasRole,
      hasPerm,
      login,
      logout,
      fetchMe,
      fetchPerms,
      refresh,
      apiFetch,
      apiJSON,
      API_BASE,
    ]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}